import React from 'react';

import {Box, Typography} from '@mui/material';
import {motion} from 'framer-motion';
import {noop} from 'lodash';
import {useTranslation} from 'react-i18next';

import {FamilyIcon, FlipIcon, PaletteIcon} from '@/app/icons';
import {AppButton, AppButtonVariant} from '@/app/shared-components';
import MdkModal from '@/app/shared-components/MdkModal/MdkModal';
import {ECardThemesIds} from '@/dataSource/src/api/cards-theme/entities';
import useWindowSize from '@/hooks/WindowSize/WindowSize';
import {COLORS} from '@/styles/colors';

import {E_CARD_NOTE, useActions} from './useActions';
import './styles';

interface ConfirmCardThemeModalProps {
  cardThemeSelected: ECardThemesIds;
  open: boolean;
  onClose: () => void;
  onContinue: () => void;
}

const ConfirmCardThemeModal: React.FC<ConfirmCardThemeModalProps> = ({
  cardThemeSelected,
  open,
  onClose = noop,
  onContinue,
}) => {
  const {t} = useTranslation();
  const {isMobile} = useWindowSize();
  const {
    y,
    scale,
    rotateY,
    controls,
    opacityFront,
    opacityBack,
    cardThemeLabel,
    cardFrontPicture,
    cardBackPicture,
    cardNote,
    flipCard,
    pressInAnimation,
    onCloseModal,
  } = useActions({cardThemeSelected, open, onClose});

  return (
    <MdkModal
      open={open}
      onClose={onCloseModal}
      isBottomSheet
      footer={
        <Box className="confirm-card-theme-modal-footer">
          <AppButton
            label={t('confirm_card_theme_selected.button', {
              theme: cardThemeLabel,
            })}
            variant={AppButtonVariant.onPrimary}
            onClick={onContinue}
          />
        </Box>
      }
      aria-labelledby="confirm-card-theme-modal">
      <Box className="confirm-card-theme-modal-container">
        <Box className="confirm-card-theme-modal-content">
          <Typography className="confirm-card-theme-modal-title">
            {t('confirm_card_theme_selected.title')}
          </Typography>
          <div
            onMouseDown={pressInAnimation}
            onMouseUp={flipCard}>
            <motion.div
              style={{
                y,
                scale,
                rotateY,
                cursor: 'pointer',
                perspective: '1000px',
              }}
              animate={controls}>
              <motion.img
                src={cardFrontPicture}
                alt="Card Front"
                style={{
                  width: '100%',
                  height: 210,
                  opacity: opacityFront,
                  rotate: '8deg',
                  backfaceVisibility: 'hidden',
                }}
              />
              <motion.img
                src={cardBackPicture}
                alt="Card Back"
                style={{
                  width: '100%',
                  height: 210,
                  opacity: opacityBack,
                  rotate: '8deg',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                }}
              />
            </motion.div>
            <motion.div
              className="absolute bottom-0 left-1/2 -translate-x-1/2 bg-black/30 rounded-full"
              style={{
                width: 180,
                height: 5,
                filter: 'blur(8px)',
                backgroundColor: COLORS.ON_OUTLINE_LIGHT,
                position: 'relative',
                right: -200,
                bottom: -22,
              }}
              animate={{
                scaleX: [1, 0.8, 1],
                scaleY: [1, 0.5, 1],
                opacity: [0.5, 0.2, 0.5],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />
          </div>

          <Box
            className="flip-content"
            onClick={flipCard}>
            <FlipIcon />
            <Typography className="flip-text">
              {isMobile
                ? t('confirm_card_theme_selected.flip_mobile')
                : t('confirm_card_theme_selected.flip_desktop')}
            </Typography>
          </Box>

          {cardNote !== E_CARD_NOTE.NONE && (
            <Box className="card-note">
              {cardNote === E_CARD_NOTE.DIY ? (
                <PaletteIcon
                  width={48}
                  height={48}
                />
              ) : (
                <FamilyIcon
                  width={48}
                  height={48}
                />
              )}
              <Typography className="card-note-text">
                {cardNote === E_CARD_NOTE.DIY
                  ? t('confirm_card_theme_selected.diy_note')
                  : t('confirm_card_theme_selected.vote_note')}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </MdkModal>
  );
};

export default ConfirmCardThemeModal;
